"use client"

import { t } from "@/languages/i18n"
import { formatRelatedProducts } from "@/utils/format-related-products"
import { Alert, Button, DatePicker, Form, FormInstance, Input, Radio, Select, Space } from "antd"
import dayjs, { Dayjs } from "dayjs"
import dynamic from "next/dynamic"
import React, { useEffect, useMemo, useState } from "react"

import ReadonlyMultiSelect from "@/(admin)/modules/campaign/components/ReadonlyMultiSelect"
import { useNormalizeToIds } from "@/(admin)/modules/campaign/hooks/useNormalizeToIds"
import { fetchStores } from "@/(admin)/modules/stores/services/store"

const CKEditorComponent = dynamic(() => import("@/lib/config/lib/Ckeditor"), { ssr: false })

// Antd popupClassName cho 2 DatePicker của form này — dùng để ẩn 2 nút nhảy năm
// («/») ở CSS (xem admin-theme.css). Header vẫn giữ nút tháng/năm bấm được (mặc
// định của rc-picker), chỉ bỏ cặp nút superPrev/superNext.
const CAMPAIGN_DATE_PANEL_CLASS = "tm-campaign-date-panel"

// Quy ước đặt tên chiến dịch mã hoá sẵn ngày: "26-09 Kitkat" = YY-MM + tên.
const NAME_DATE_PATTERN = /^(\d{2})-(\d{2})\b/

function parseDateFromName(name?: string | null): { month: number; year: number } | null {
  if (!name) return null
  const match = NAME_DATE_PATTERN.exec(name.trim())
  if (!match) return null
  const month = Number(match[2])
  if (month < 1 || month > 12) return null
  return { month, year: 2000 + Number(match[1]) }
}

/** Khoảng [đầu tháng 00:00, cuối tháng 23:59] cho 1 tháng/năm cho trước. */
function monthRange(year: number, month1to12: number) {
  const start = dayjs(`${year}-${String(month1to12).padStart(2, "0")}-01`).startOf("month")
  const end = start.endOf("month").hour(23).minute(59).second(0).millisecond(0)
  return { start, end }
}

/** Khoảng tuần kế tiếp (Thứ 2 → Chủ nhật), giờ kết thúc 23:59. */
function nextWeekRange() {
  const now = dayjs()
  const dow = now.day() // 0 = CN .. 6 = T7
  const daysUntilNextMonday = ((8 - dow) % 7) || 7
  const start = now.add(daysUntilNextMonday, "day").startOf("day")
  const end = start.add(6, "day").hour(23).minute(59).second(0).millisecond(0)
  return { start, end }
}

/** "tháng 9 năm 2026" — dùng trong cả cảnh báo lệch ngày lẫn gợi ý điền theo tên. */
function monthYearLabel(d: Dayjs) {
  return `tháng ${d.month() + 1} năm ${d.year()}`
}

/**
 * Cảnh báo khi ngày bắt đầu cách "hôm nay" quá xa (>~20 ngày) hoặc chiến dịch coi
 * như đã hết hạn — chặn lỗi gõ nhầm năm/tháng (vd định gõ 2025 lại thành 2026).
 * Không dùng emoji trong message: <Alert showIcon> đã tự vẽ icon, thêm emoji bị
 * lặp icon (2 icon cùng lúc).
 */
function describeStartWarning(dateFrom?: string, dateTo?: string): string | null {
  if (!dateFrom) return null
  const start = dayjs(dateFrom)
  if (!start.isValid()) return null

  const today = dayjs().startOf("day")
  const diffDays = start.startOf("day").diff(today, "day")

  if (diffDays < 0) {
    // Start ở quá khứ nhưng end vẫn hôm nay/tương lai → chiến dịch đang chạy
    // bình thường (vd tạo campaign bắt đầu từ giữa tháng), không phải lỗi gõ
    // nhầm. Chỉ cảnh báo khi cả khoảng đã trôi qua (hoặc chưa có end).
    const end = dateTo ? dayjs(dateTo) : null
    const stillRunningOrUpcoming = end && end.isValid() && !end.isBefore(today)
    if (stillRunningOrUpcoming) return null
    return `Ngày bắt đầu đang ở trong quá khứ (${monthYearLabel(start)}). Bạn có chắc không?`
  }
  if (diffDays <= 20) return null

  let relative: string
  if (diffDays < 30) {
    const weeks = Math.round(diffDays / 7)
    relative = weeks <= 1 ? "sau 1 tuần" : `sau ${weeks} tuần`
  } else {
    const months = Math.round(diffDays / 30)
    relative = months <= 1 ? "sau 1 tháng" : `sau ${months} tháng`
  }

  return `Chiến dịch bắt đầu ${relative}. Bạn có chắc là ${monthYearLabel(start)}?`
}

interface Props {
  form: FormInstance
  onFormChange: () => void
}

const CampaignUpsertForm: React.FC<Props> = ({ form, onFormChange }) => {
  const relatedProductsValue = Form.useWatch("relatedProducts", form)
  const postValue = Form.useWatch("posts", form)
  const subscriberValue = Form.useWatch("campaign_subscribers", form)
  const nameValue = Form.useWatch("name", form)
  const dateFromValue = Form.useWatch("dateFrom", form)
  const dateToValue = Form.useWatch("dateTo", form)

  const [stores, setStores] = useState<{ label: string; value: number }[]>([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  const applyDateRange = (start: Dayjs, end: Dayjs) => {
    form.setFieldsValue({ dateFrom: start.toISOString(), dateTo: end.toISOString() })
    onFormChange()
  }

  const parsedNameDate = useMemo(() => parseDateFromName(nameValue), [nameValue])
  const startWarning = useMemo(
    () => describeStartWarning(dateFromValue, dateToValue),
    [dateFromValue, dateToValue],
  )

  // ✅ normalize hooks
  const postObjects = useNormalizeToIds(form, "posts", postValue)
  const subscriberObjects = useNormalizeToIds(form, "campaign_subscribers", subscriberValue)

  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true)
      try {
        const [storesRes] = await Promise.all([
          fetchStores(
            { pageSize: "100", sort: "name:asc" },
            { fields: ["name"], populate: null, publicationState: "preview", locale: "vi" },
          ),
        ])

        if (storesRes?.data) {
          setStores(
            storesRes.data.map((s: any) => ({
              label: s.attributes.name,
              value: s.id,
            })),
          )
        }
      } catch (err) {
        console.error(err)
      } finally {
        setLoadingOptions(false)
      }
    }

    loadOptions()
  }, [])

  // fix relatedProducts — runs whenever the watched value changes (including after async loadData)
  useEffect(() => {
    if (relatedProductsValue && typeof relatedProductsValue === "string" && !relatedProductsValue.includes("<p>")) {
      form.setFieldsValue({
        relatedProducts: formatRelatedProducts(relatedProductsValue),
      })
    }
  }, [relatedProductsValue, form])

  return (
    <Form form={form} layout="vertical" onValuesChange={onFormChange} initialValues={{ tamdaExpressCampaign: false }}>
      <div className="grid grid-cols-2 gap-4">
        <Form.Item name="name" label={t("Name")} rules={[{ required: true, message: t("Vui lòng nhập tên campaign") }]}>
          <Input />
        </Form.Item>

        <Form.Item name="tamdaExpressCampaign" label={t("Tamda Express Campaign")}>
          <Radio.Group>
            <Radio.Button value={false}>{t("No")}</Radio.Button>
            <Radio.Button value={true}>{t("Yes")}</Radio.Button>
          </Radio.Group>
        </Form.Item>
      </div>

      {/* Tên đã mã hoá ngày (vd "26-09 Kitkat") → gợi ý điền theo tên, tên là nguồn,
          lịch chỉ để xác nhận/chỉnh lại nếu cần. */}
      {parsedNameDate && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={t("Tên chiến dịch có vẻ mã hoá ngày tháng")}
          description={`${t("Tên bắt đầu bằng")} "${String(nameValue).slice(0, 5)}" → ${monthYearLabel(monthRange(parsedNameDate.year, parsedNameDate.month).start)}.`}
          action={
            <Button
              size="small"
              type="primary"
              onClick={() => {
                const { start, end } = monthRange(parsedNameDate.year, parsedNameDate.month)
                applyDateRange(start, end)
              }}
            >
              {t("Điền theo tên")}
            </Button>
          }
        />
      )}

      <div className="mb-3 flex items-center gap-2">
        <span className="text-xs" style={{ color: "var(--admin-muted-fg)" }}>
          {t("Chọn nhanh")}:
        </span>
        <Space size="small">
          <Button
            size="small"
            onClick={() => {
              const now = dayjs()
              const { start, end } = monthRange(now.year(), now.month() + 1)
              applyDateRange(start, end)
            }}
          >
            {t("Tháng này")}
          </Button>
          <Button
            size="small"
            onClick={() => {
              const next = dayjs().add(1, "month")
              const { start, end } = monthRange(next.year(), next.month() + 1)
              applyDateRange(start, end)
            }}
          >
            {t("Tháng sau")}
          </Button>
          <Button
            size="small"
            onClick={() => {
              const { start, end } = nextWeekRange()
              applyDateRange(start, end)
            }}
          >
            {t("Tuần sau")}
          </Button>
        </Space>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          name="dateFrom"
          label={t("Date From")}
          getValueProps={v => ({ value: v ? dayjs(v) : undefined })}
          normalize={v => (v ? v.toISOString() : "")}
        >
          <DatePicker
            showTime={{ format: "HH:mm" }}
            format="DD/MM/YYYY HH:mm"
            className="w-full"
            placeholder={t("Chọn thời điểm")}
            popupClassName={CAMPAIGN_DATE_PANEL_CLASS}
          />
        </Form.Item>

        <Form.Item
          name="dateTo"
          label={t("Date To")}
          getValueProps={v => ({ value: v ? dayjs(v) : undefined })}
          normalize={v => (v ? v.toISOString() : "")}
        >
          <DatePicker
            // Ngày kết thúc mặc định 23:59 khi user chỉ chọn ngày, chưa đụng tới giờ
            // — tránh chiến dịch "hết hạn" ngay từ 00:00 của đúng ngày kết thúc.
            showTime={{ format: "HH:mm", defaultValue: dayjs("23:59", "HH:mm") }}
            format="DD/MM/YYYY HH:mm"
            className="w-full"
            placeholder={t("Chọn thời điểm")}
            popupClassName={CAMPAIGN_DATE_PANEL_CLASS}
          />
        </Form.Item>
      </div>

      {startWarning && (
        <Alert className="mb-3 -mt-2" type="warning" showIcon message={startWarning} />
      )}

      <Form.Item name="tamdaStores" label={t("Stores")}>
        <Select mode="multiple" options={stores} loading={loadingOptions} />
      </Form.Item>

      {/* POSTS */}
      <Form.Item name="posts" label={t("Posts")}>
        <ReadonlyMultiSelect
          value={postValue}
          objects={postObjects}
          getLabel={(p: any) => p.title}
          buildHref={(p: any) => `/admin/posts/edit?id=${p?.id}`}
        />
      </Form.Item>

      {/* SUBSCRIBERS */}
      <Form.Item name="campaign_subscribers" label={t("Subscribers")}>
        <ReadonlyMultiSelect
          value={subscriberValue}
          objects={subscriberObjects}
          getLabel={(s: any) => `${s.fullname}${s.phone ? ` — Phone: ${s.phone}` : ""}`}
          buildHref={(s: any) => `/admin/campaign-subscribers/edit?id=${s?.id}`}
          showSearch
        />
      </Form.Item>

      <Form.Item name="relatedProducts" label={t("Related Products")}>
        <CKEditorComponent
          value={formatRelatedProducts(relatedProductsValue)}
          // Field này chỉ chứa danh sách mã EAN thô (mỗi dòng 1 mã) — ép dán luôn
          // thành plain text để copy 1 cột Excel không bị Table plugin dựng thành
          // bảng thật (Excel kèm sẵn HTML table trong clipboard).
          plainTextPaste
          onChange={(data: string) => {
            form.setFieldValue("relatedProducts", data)
            onFormChange()
          }}
        />
      </Form.Item>
    </Form>
  )
}

export default CampaignUpsertForm
